import React from 'react';

/*
  Dashboard — business account dashboard extracted from App.js.
  Props: navigateTo, setLegalFirstTime, setLifeFinanceFirstTime, setLifeFinanceActiveTab
*/
const Dashboard = ({ navigateTo, setLegalFirstTime, setLifeFinanceFirstTime, setLifeFinanceActiveTab }) => {

  // Business dashboard: finance-only. Non-finance tools (Legal GPT, Customer Insights,
  // Career Co-Pilot) are accessible via The Market — not from the core app.
  const agents = [
    {
      name: 'FinanceGPT',
      domain: 'Financial Intelligence',
      abbr: 'FG',
      desc: 'Upload statements, chat with your numbers, and get AI-driven financial insights',
      page: 'life-finance',
      color: '#00C27C',
      subLinks: [
        { label: 'CA Documents', tab: 'ca-docs' },
      ],
    },
    {
      name: 'Debt / EMI Intelligence',
      domain: 'Loan Analysis',
      abbr: 'DI',
      desc: 'Prepay or invest? Compare interest saved vs returns vs tax deductions. Rank multiple loans by effective cost.',
      page: 'debt-intelligence',
      color: '#3B82F6',
    },
    {
      name: 'Cash-Flow Reality',
      domain: 'Cash Flow Analysis',
      abbr: 'CF',
      desc: 'Upload bank statements and get your true discretionary income with concrete, actionable insights.',
      page: 'cashflow-reality',
      color: '#F59E0B',
    },
  ];

  // eslint-disable-next-line no-unused-vars
  const lightCard = {
    backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: '14px', transition: 'all 0.22s ease',
  };
  const sectionLabel = () => ({
    color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px',
  });

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div style={{ marginBottom: '48px' }}>
          <p style={sectionLabel()}>Dashboard</p>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.025em', margin: 0 }}>Finance Dashboard</h1>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {agents.map((a, i) => {
            const c = a.color;
            return (
              <div key={i} style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', padding: '28px', position: 'relative', overflow: 'hidden', transition: 'transform var(--t-base), box-shadow var(--t-base), border-color var(--t-base)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = `${c}50`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', backgroundColor: c }} />
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: `${c}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', border: `1px solid ${c}30` }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: c, letterSpacing: '0.03em' }}>{a.abbr}</span>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{a.name}</h3>
                <div style={{ fontSize: '11px', fontWeight: 700, color: c, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>{a.domain}</div>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: '20px' }}>{a.desc}</p>

                {/* Sub-feature pills (e.g. CA Documents inside FinanceGPT) */}
                {a.subLinks && a.subLinks.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '18px' }}>
                    {a.subLinks.map(sl => (
                      <button
                        key={sl.tab}
                        onClick={() => {
                          setLifeFinanceActiveTab(sl.tab);
                          setLifeFinanceFirstTime(false);
                          navigateTo(a.page);
                        }}
                        style={{
                          fontSize: '11px', fontWeight: 700, color: c,
                          background: `${c}12`, border: `1px solid ${c}35`,
                          borderRadius: '20px', padding: '4px 11px',
                          cursor: 'pointer', fontFamily: 'inherit',
                          letterSpacing: '0.04em', transition: 'all 0.18s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${c}25`; e.currentTarget.style.borderColor = `${c}70`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${c}12`; e.currentTarget.style.borderColor = `${c}35`; }}
                      >{sl.label} ↗</button>
                    ))}
                  </div>
                )}

                {a.page ? (
                  <button
                    onClick={() => {
                      if (a.page === 'life-finance') { setLifeFinanceFirstTime(true); setLifeFinanceActiveTab('chat'); }
                      navigateTo(a.page);
                    }}
                    style={{ fontSize: '13px', fontWeight: 600, color: c, background: 'none', border: 'none', padding: 0, cursor: 'pointer', transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >Launch Agent →</button>
                ) : (
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Coming soon</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
